import {createStyles} from '@mantine/core';
import * as d3 from 'd3';
import React, {useMemo} from 'react';

import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {Cell, Column, Table} from 'ui-library/simple-table/SimpleTable';

import {DISPLAY_ROW_LIMIT} from '../FilePreviewConstants';

interface CSVRendererProps {
  content: string;
  delimiter: string;
}

const useStyles = createStyles((theme) => ({
  tableWrapper: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
    border: `1px solid ${theme.colors.gray[2]}`,
    borderRadius: theme.radius.sm,
    boxSizing: 'border-box',
  },
  table: {
    // Triple-class specificity to override `.react-tab-container .simpleTable { margin-left: -12px }`
    '&&&': {
      margin: 0,
    },
  },
  cell: {
    fontSize: theme.fontSizes.md,
    lineHeight: 1.5,
    height: 36,
  },
}));

export default function CSVRenderer({content, delimiter}: CSVRendererProps): JSX.Element {
  const {classes} = useStyles();

  const parsed = useMemo(() => {
    const rows = d3.dsvFormat(delimiter).parseRows(content);
    if (!rows || rows.length === 0) {
      return null;
    }
    const headerRow = rows[0];
    const totalRows = rows.length - 1;
    const displayedRows = rows.slice(1, DISPLAY_ROW_LIMIT + 1);
    return {headerRow, displayedRows, totalRows};
  }, [content, delimiter]);

  if (!parsed) {
    return <InlineBanner bannerType={InlineBannerType.INFO} text="The CSV file is empty." />;
  }

  const {headerRow, displayedRows, totalRows} = parsed;
  const hasMoreRows = totalRows > DISPLAY_ROW_LIMIT;

  return (
    <div className={classes.tableWrapper}>
      {hasMoreRows && (
        <InlineBanner
          bannerType={InlineBannerType.WARNING}
          text={`Showing the first ${DISPLAY_ROW_LIMIT} rows out of ${totalRows}`}
          supportingInfo="Download to view the complete data."
        />
      )}
      <Table
        calculateInView={true}
        className={classes.table}
        emptyTableText="No data"
        fitContent={true}
        frozenHeader={true}
        getRowKey={(rowIndex: number) => rowIndex}
        isCompact={true}
        numRows={displayedRows.length}
        overflowVisible={true}
        showBorder={false}
      >
        {headerRow.map((header, columnIndex) => (
          <Column
            key={columnIndex}
            header={header}
            renderCell={(rowIndex: number) => (
              <Cell className={classes.cell}>{displayedRows[rowIndex]?.[columnIndex] || ''}</Cell>
            )}
          />
        ))}
      </Table>
    </div>
  );
}
